// Finance views (M5-03; 11 §Revenue attribution, §Planning; 05 §finance): budget vs actual over
// the org tree, cost / revenue / margin per seat, team and client, clients and their seat shares,
// revenue, compensation (restricted), recurring expenses, the nightly forecast, scenarios, the
// headcount plan, CSV/XLSX exports with chart-of-accounts categories, and the finance relations
// (`company#finance_admin`, `seat#comp_viewer`). Base plan (`core`), like the finance controls.
//
// Access (05 §finance), decided per row by the handlers (`member` here):
//   - totals (budget vs actual, forecasts): company managers and finance admins see the whole
//     tree; a seat holder sees their seat, the seats under it and the teams their seat leads;
//   - compensation, benefits, revenue, margins, scenarios, the headcount plan and exports:
//     `finance_admin` and the company owner; `seat#comp_viewer` reads those of its seat;
//   - finance writes: `finance_admin`; granting the finance relations: the company owner.
// A seat-level figure shown to someone who may not read that seat's compensation leaves
// compensation and benefits out (`restricted_hidden`); team and company totals include them.
import { z } from "zod";
import {
  CategoryKind,
  CompensationCadence,
  Currency,
  FinanceRecordStatus,
  FinanceRelation,
  HeadcountHolderKind,
  RecurringCadence,
  RecurringExpenseKind,
  RevenueKind,
  RunEffortTier,
} from "../enums";
import {
  Category,
  Client,
  Compensation,
  FinanceSourceRef,
  ForecastSnapshot,
  HeadcountPlan,
  RecurringExpense,
  RevenueEvent,
  Scenario,
  ScenarioChangeJson,
} from "../finance-rows";
import {
  Decimal,
  DecimalInput,
  IsoDate,
  LIMITS,
  MinorAmount,
  MonthKey,
  PeriodKey,
  Timestamp,
  text,
  Uuid,
} from "../primitives";
import { CompanyItemParams, CompanyParams, PageQuery, page } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;
const ViewPeriodType = z.enum(["month", "quarter", "year"]);
const name = text(LIMITS.name);

// Finance relations ------------------------------------------------------------------------------

export const FinanceGrant = z
  .object({ relation: FinanceRelation, membership_id: Uuid, seat_id: Uuid.nullable() })
  .meta({ id: "FinanceGrant" });
/** `comp_viewer` names its seat; `finance_admin` is on the company (no seat). */
export const FinanceGrantInput = z
  .strictObject({
    relation: FinanceRelation,
    membership_id: Uuid,
    seat_id: Uuid.nullable().optional(),
  })
  .meta({ id: "FinanceGrantInput" });
export const FinanceAccessView = z
  .object({
    grants: z.array(FinanceGrant),
    me: z.object({
      finance_admin: z.boolean(),
      owner: z.boolean(),
      comp_viewer_seat_ids: z.array(Uuid),
      /**
       * The caller reads budget vs actual and the forecast (whole company, or a held seat's
       * subtree): the routes' own totals check, so the Chart sidebar shows what will answer.
       */
      reads_totals: z.boolean(),
      /** The caller reads money per seat, scenarios, the headcount plan and exports. */
      reads_restricted: z.boolean(),
    }),
  })
  .meta({ id: "FinanceAccessView" });

// Categories, clients ------------------------------------------------------------------------------

export const CategoryInput = z
  .strictObject({
    name,
    kind: CategoryKind,
    code: z.string().trim().min(1).max(LIMITS.key).nullable().optional(),
    cost_kinds: z.array(z.string().max(LIMITS.key)).max(30).optional(),
  })
  .meta({ id: "CategoryInput" });
export const CategoryPatch = z
  .strictObject({
    name: name.optional(),
    code: z.string().trim().min(1).max(LIMITS.key).nullable().optional(),
    cost_kinds: z.array(z.string().max(LIMITS.key)).max(30).optional(),
    status: FinanceRecordStatus.optional(),
  })
  .meta({ id: "CategoryPatch" });

export const ClientSeatShare = z.strictObject({ seat_id: Uuid, share: DecimalInput });
export const ClientInput = z
  .strictObject({ name, seats: z.array(ClientSeatShare).max(LIMITS.list).optional() })
  .meta({ id: "ClientInput" });
export const ClientPatch = z
  .strictObject({
    name: name.optional(),
    status: FinanceRecordStatus.optional(),
    seats: z.array(ClientSeatShare).max(LIMITS.list).optional().describe("Replaces the set"),
  })
  .meta({ id: "ClientPatch" });
export const ClientDetail = Client.extend({
  seats: z.array(z.object({ seat_id: Uuid, share: Decimal })),
}).meta({ id: "ClientDetail" });

// Revenue, compensation, recurring expenses --------------------------------------------------------

export const RevenueInput = z
  .strictObject({
    occurred_at: Timestamp,
    amount_minor: MinorAmount.refine((n) => n !== 0, "a revenue row is never zero"),
    currency: Currency,
    kind: RevenueKind,
    client_id: Uuid.nullable().optional(),
    seat_id: Uuid.nullable().optional(),
    project_ref: z.string().trim().min(1).max(LIMITS.name).nullable().optional(),
    category_id: Uuid.nullable().optional(),
    source_ref: FinanceSourceRef.nullable().optional(),
    note: z.string().max(LIMITS.text).nullable().optional(),
  })
  .meta({ id: "RevenueInput" });
export const RevenueQuery = PageQuery.extend({
  period_key: MonthKey.optional(),
  client_id: Uuid.optional(),
  seat_id: Uuid.optional(),
});

export const CompensationInput = z
  .strictObject({
    seat_id: Uuid,
    membership_id: Uuid.nullable().optional(),
    amount_minor: MinorAmount.min(0),
    currency: Currency,
    cadence: CompensationCadence,
    effective_from: IsoDate,
    effective_to: IsoDate.nullable().optional(),
    category_id: Uuid.nullable().optional(),
    note: z.string().max(LIMITS.text).nullable().optional(),
  })
  .meta({ id: "CompensationInput" });
export const CompensationPatch = CompensationInput.omit({ seat_id: true, cadence: true })
  .partial()
  .meta({ id: "CompensationPatch" });
export const CompensationQuery = PageQuery.extend({ seat_id: Uuid.optional() });

export const RecurringExpenseInput = z
  .strictObject({
    name,
    kind: RecurringExpenseKind,
    amount_minor: MinorAmount.min(0),
    currency: Currency,
    cadence: RecurringCadence.optional(),
    seat_id: Uuid.nullable().optional(),
    team_id: Uuid.nullable().optional(),
    start_month: MonthKey,
    end_month: MonthKey.nullable().optional(),
    category_id: Uuid.nullable().optional(),
  })
  .meta({ id: "RecurringExpenseInput" });
export const RecurringExpensePatch = RecurringExpenseInput.omit({ kind: true })
  .partial()
  .meta({ id: "RecurringExpensePatch" });
export const RecurringExpenseQuery = PageQuery.extend({ kind: RecurringExpenseKind.optional() });

// Views -------------------------------------------------------------------------------------------

export const FinanceViewQuery = z.object({
  period_type: ViewPeriodType.optional().describe("Default month"),
  period_key: PeriodKey,
});

export const FinanceNodeKind = z.enum(["company", "team", "seat"]);

export const BudgetVsActualNode = z
  .object({
    kind: FinanceNodeKind,
    id: Uuid,
    name: z.string(),
    parent_id: Uuid.nullable(),
    budget_minor: MinorAmount.nullable(),
    actual_minor: MinorAmount,
    variance_minor: MinorAmount.nullable(),
    utilization: Decimal.nullable(),
    by_kind: z.record(z.string(), MinorAmount),
    restricted_hidden: z
      .boolean()
      .describe("true when compensation and benefits are left out of this seat's figures"),
  })
  .meta({ id: "BudgetVsActualNode" });
export const BudgetVsActual = z
  .object({
    period_type: ViewPeriodType,
    period_key: z.string(),
    currency: Currency,
    nodes: z.array(BudgetVsActualNode),
  })
  .meta({ id: "BudgetVsActual" });

export const MarginQuery = FinanceViewQuery.extend({ by: z.enum(["seat", "team", "client"]) });
export const MarginRow = z
  .object({
    subject_kind: z.enum(["seat", "team", "client", "company"]),
    subject_id: Uuid,
    name: z.string(),
    cost_minor: MinorAmount,
    revenue_minor: MinorAmount,
    margin_minor: MinorAmount,
    margin_ratio: Decimal.nullable(),
  })
  .meta({ id: "MarginRow" });
export const Margins = z
  .object({
    period_type: ViewPeriodType,
    period_key: z.string(),
    by: z.enum(["seat", "team", "client"]),
    currency: Currency,
    rows: z.array(MarginRow),
    total: MarginRow,
  })
  .meta({ id: "Margins" });

export const ForecastQuery = z.object({ period_key: MonthKey.optional() });
export const ForecastView = z
  .object({
    period_key: z.string(),
    as_of: IsoDate.nullable(),
    rows: z.array(ForecastSnapshot),
  })
  .meta({ id: "ForecastView" });

// Agent spend (SPEND-KPIS) ---------------------------------------------------------------------------

export const AgentSpendQuery = z.object({
  period_key: MonthKey.optional().describe("The month (default: the current one)"),
});

/** One figure set: the period's day, week and month, their averages, finished tasks. */
const SpendFigures = z.object({
  today_minor: MinorAmount,
  week_minor: MinorAmount,
  period_minor: MinorAmount,
  /** The period's spend over its days so far (the whole month for a past one). */
  avg_day_minor: MinorAmount,
  /** The period's spend over its weeks so far (days / 7, at least one day). */
  avg_week_minor: MinorAmount,
  tasks_done: z.int().min(0),
  /** The period's spend over its finished tasks; null without any. */
  per_task_minor: MinorAmount.nullable(),
});

export const AgentSpendBudget = z
  .object({
    budget_id: Uuid,
    inherited: z.boolean(),
    limit_minor: MinorAmount,
    /** The ledger's agent spend in the budget's own period over its limit. */
    used: z.number(),
    status: z.enum(["on_track", "ahead_of_pace", "reserve_reached", "no_budget"]),
    projected_minor: MinorAmount.nullable(),
  })
  .meta({ id: "AgentSpendBudget" });

/**
 * RUN-EFFORT: the period's run cost by effort tier (runs started in the period; `untiered`: runs
 * admitted before tiers). From the runs' own cost, so it can differ a little from the ledger's.
 */
export const AgentSpendTier = z
  .object({
    tier: z.union([RunEffortTier, z.literal("untiered")]),
    runs: z.int().min(0),
    amount_minor: MinorAmount,
  })
  .meta({ id: "AgentSpendTier" });
export type AgentSpendTier = z.infer<typeof AgentSpendTier>;

export const AgentSpendRow = z
  .object({
    membership_id: Uuid,
    name: z.string(),
    kind: z.enum(["digital_employee", "agent"]),
    seat_id: Uuid.nullable(),
    seat_title: z.string().nullable(),
    team_id: Uuid.nullable(),
    ...SpendFigures.shape,
    budget: AgentSpendBudget.nullable(),
    /** RUN-EFFORT: the period's run cost per tier (tiers with runs only). */
    by_tier: z.array(AgentSpendTier),
  })
  .meta({ id: "AgentSpendRow" });

export const AgentSpend = z
  .object({
    period_key: z.string(),
    week_key: z.string(),
    today: IsoDate,
    currency: Currency,
    /** When the snapshot job last summed the ledger (null: never). */
    computed_at: Timestamp.nullable(),
    /** The company's (or, for a partial reader, the visible agents') totals. */
    totals: SpendFigures,
    rows: z.array(AgentSpendRow),
    /** RUN-EFFORT: the period's run cost per tier, same scope as `totals`. */
    by_tier: z.array(AgentSpendTier),
    /** Spend per day over the last 30 days, oldest first, same scope as `totals`. */
    trend: z.array(z.object({ day: IsoDate, amount_minor: MinorAmount })),
  })
  .meta({ id: "AgentSpend" });
export type AgentSpend = z.infer<typeof AgentSpend>;

// Scenarios, headcount plan, exports -----------------------------------------------------------------

export const ScenarioInput = z
  .strictObject({
    name,
    base_period: MonthKey,
    changes: z.array(ScenarioChangeJson).min(1).max(50),
  })
  .meta({ id: "ScenarioInput" });

/** ACTIONS: edit a scenario; what is given replaces the saved value, then it is re-evaluated. */
export const ScenarioPatch = ScenarioInput.partial()
  .refine((p) => Object.keys(p).length > 0, { message: "at least one field" })
  .meta({ id: "ScenarioPatch" });

export const HeadcountPlanInput = z
  .strictObject({
    title: name,
    team_id: Uuid.nullable().optional(),
    seat_id: Uuid.nullable().optional(),
    holder_kind: HeadcountHolderKind,
    start_month: MonthKey,
    end_month: MonthKey.nullable().optional(),
    monthly_cost_minor: MinorAmount.min(0),
    currency: Currency,
    note: z.string().max(LIMITS.text).nullable().optional(),
  })
  .meta({ id: "HeadcountPlanInput" });
export const HeadcountPlanPatch = HeadcountPlanInput.partial()
  .extend({ status: FinanceRecordStatus.optional() })
  .meta({ id: "HeadcountPlanPatch" });
export const HeadcountPlanQuery = PageQuery.extend({ status: FinanceRecordStatus.optional() });

const HolderCounts = z.object({
  person: z.int().min(0),
  digital_employee: z.int().min(0),
});
export const HeadcountDiff = z
  .object({
    month: z.string(),
    currency: Currency,
    planned_cost_minor: MinorAmount,
    teams: z.array(
      z.object({
        team_id: Uuid.nullable(),
        name: z.string(),
        planned: HolderCounts,
        live: HolderCounts.extend({ open: z.int().min(0) }),
        // planned − live: negative when the chart holds more seats than the plan (M5-04).
        delta: z.object({ person: z.int(), digital_employee: z.int() }),
      }),
    ),
    lines: z.array(
      z.object({
        id: Uuid,
        title: z.string(),
        seat_id: Uuid.nullable(),
        status: z.enum(["matched", "holder_mismatch", "unfilled", "planned"]),
      }),
    ),
  })
  .meta({ id: "HeadcountDiff" });
export const HeadcountDiffQuery = z.object({ month: MonthKey });

export const FinanceExportInput = z
  .strictObject({
    kind: z.enum(["cost_events", "revenue_events", "rollup"]),
    format: z.enum(["csv", "xlsx"]),
    period_from: MonthKey,
    period_to: MonthKey,
  })
  .meta({ id: "FinanceExportInput" });

export const financeViewRoutes = {
  "financeAccess.list": defineRoute({
    ...core,
    id: "financeAccess.list",
    method: "get",
    path: `${base}/finance/access`,
    tag: "finance",
    summary: "Who holds finance_admin and comp_viewer, and what the caller holds",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: FinanceAccessView },
    errors: [],
    emits: [],
  }),
  "financeAccess.grant": defineRoute({
    ...core,
    id: "financeAccess.grant",
    method: "post",
    path: `${base}/finance/access`,
    tag: "finance",
    summary: "Grant finance_admin (company) or comp_viewer (one seat) to a person (owner only)",
    access: "manager",
    params: CompanyParams,
    body: FinanceGrantInput,
    idempotency: "optional",
    response: { status: 201, schema: FinanceGrant },
    errors: [],
    emits: ["access.granted"],
  }),
  "financeAccess.revoke": defineRoute({
    ...core,
    id: "financeAccess.revoke",
    method: "post",
    path: `${base}/finance/access/revoke`,
    tag: "finance",
    summary: "Revoke a finance relation (owner only)",
    access: "manager",
    params: CompanyParams,
    body: FinanceGrantInput,
    idempotency: "optional",
    response: { status: 200, schema: FinanceGrant },
    errors: [],
    emits: ["access.revoked"],
  }),
  "categories.list": defineRoute({
    ...core,
    id: "categories.list",
    method: "get",
    path: `${base}/finance/categories`,
    tag: "finance",
    summary: "Chart-of-accounts categories",
    access: "member",
    params: CompanyParams,
    query: PageQuery,
    response: { status: 200, schema: page(Category) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "categories.create": defineRoute({
    ...core,
    id: "categories.create",
    method: "post",
    path: `${base}/finance/categories`,
    tag: "finance",
    summary: "Add a category (finance_admin)",
    access: "member",
    params: CompanyParams,
    body: CategoryInput,
    idempotency: "optional",
    response: { status: 201, schema: Category },
    errors: ["conflict"],
    emits: ["category.changed"],
  }),
  "categories.update": defineRoute({
    ...core,
    id: "categories.update",
    method: "patch",
    path: `${base}/finance/categories/{id}`,
    tag: "finance",
    summary: "Rename, re-code, re-map or archive a category (finance_admin)",
    access: "member",
    params: CompanyItemParams,
    body: CategoryPatch,
    response: { status: 200, schema: Category },
    errors: ["conflict"],
    emits: ["category.changed"],
  }),
  "clients.list": defineRoute({
    ...core,
    id: "clients.list",
    method: "get",
    path: `${base}/clients`,
    tag: "finance",
    summary: "Clients with their seat shares",
    access: "member",
    params: CompanyParams,
    query: PageQuery,
    response: { status: 200, schema: page(ClientDetail) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "clients.create": defineRoute({
    ...core,
    id: "clients.create",
    method: "post",
    path: `${base}/clients`,
    tag: "finance",
    summary: "Add a client and the seats that serve it (finance_admin)",
    access: "member",
    params: CompanyParams,
    body: ClientInput,
    idempotency: "optional",
    response: { status: 201, schema: ClientDetail },
    errors: ["conflict"],
    emits: ["client.changed"],
  }),
  "clients.update": defineRoute({
    ...core,
    id: "clients.update",
    method: "patch",
    path: `${base}/clients/{id}`,
    tag: "finance",
    summary: "Rename or archive a client, or replace its seat shares (finance_admin)",
    access: "member",
    params: CompanyItemParams,
    body: ClientPatch,
    response: { status: 200, schema: ClientDetail },
    errors: ["conflict"],
    emits: ["client.changed"],
  }),
  "revenueEvents.list": defineRoute({
    ...core,
    id: "revenueEvents.list",
    method: "get",
    path: `${base}/revenue-events`,
    tag: "finance",
    summary: "Revenue rows (restricted), newest first",
    access: "member",
    params: CompanyParams,
    query: RevenueQuery,
    response: { status: 200, schema: page(RevenueEvent) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "revenueEvents.create": defineRoute({
    ...core,
    id: "revenueEvents.create",
    method: "post",
    path: `${base}/revenue-events`,
    tag: "finance",
    summary: "Post revenue (finance_admin); a correction is a negative row",
    access: "member",
    params: CompanyParams,
    body: RevenueInput,
    idempotency: "required",
    response: { status: 201, schema: RevenueEvent },
    errors: [],
    emits: ["revenue.posted"],
  }),
  "compensation.list": defineRoute({
    ...core,
    id: "compensation.list",
    method: "get",
    path: `${base}/compensation`,
    tag: "finance",
    summary: "Compensation rows the caller may read (restricted)",
    access: "member",
    params: CompanyParams,
    query: CompensationQuery,
    response: { status: 200, schema: page(Compensation) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "compensation.create": defineRoute({
    ...core,
    id: "compensation.create",
    method: "post",
    path: `${base}/compensation`,
    tag: "finance",
    summary: "Set a seat's compensation from a date (finance_admin)",
    access: "member",
    params: CompanyParams,
    body: CompensationInput,
    idempotency: "optional",
    response: { status: 201, schema: Compensation },
    errors: [],
    emits: ["compensation.changed"],
  }),
  "compensation.update": defineRoute({
    ...core,
    id: "compensation.update",
    method: "patch",
    path: `${base}/compensation/{id}`,
    tag: "finance",
    summary: "Change or end a compensation row (finance_admin)",
    access: "member",
    params: CompanyItemParams,
    body: CompensationPatch,
    response: { status: 200, schema: Compensation },
    errors: [],
    emits: ["compensation.changed"],
  }),
  "recurringExpenses.list": defineRoute({
    ...core,
    id: "recurringExpenses.list",
    method: "get",
    path: `${base}/recurring-expenses`,
    tag: "finance",
    summary: "Benefits (restricted), overhead and software subscriptions",
    access: "member",
    params: CompanyParams,
    query: RecurringExpenseQuery,
    response: { status: 200, schema: page(RecurringExpense) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "recurringExpenses.create": defineRoute({
    ...core,
    id: "recurringExpenses.create",
    method: "post",
    path: `${base}/recurring-expenses`,
    tag: "finance",
    summary: "Add a recurring expense on a seat, a team or the company (finance_admin)",
    access: "member",
    params: CompanyParams,
    body: RecurringExpenseInput,
    idempotency: "optional",
    response: { status: 201, schema: RecurringExpense },
    errors: [],
    emits: ["recurring_expense.changed"],
  }),
  "recurringExpenses.update": defineRoute({
    ...core,
    id: "recurringExpenses.update",
    method: "patch",
    path: `${base}/recurring-expenses/{id}`,
    tag: "finance",
    summary: "Change or end a recurring expense (finance_admin)",
    access: "member",
    params: CompanyItemParams,
    body: RecurringExpensePatch,
    response: { status: 200, schema: RecurringExpense },
    errors: [],
    emits: ["recurring_expense.changed"],
  }),
  "finance.budgetVsActual": defineRoute({
    ...core,
    id: "finance.budgetVsActual",
    method: "get",
    path: `${base}/finance/budget-vs-actual`,
    tag: "finance",
    summary: "Budget vs actual over the company → team → seat tree for a period",
    access: "member",
    params: CompanyParams,
    query: FinanceViewQuery,
    response: { status: 200, schema: BudgetVsActual },
    errors: [],
    emits: [],
  }),
  "finance.margins": defineRoute({
    ...core,
    id: "finance.margins",
    method: "get",
    path: `${base}/finance/margins`,
    tag: "finance",
    summary: "Cost, revenue and margin per seat, team or client (restricted)",
    access: "member",
    params: CompanyParams,
    query: MarginQuery,
    response: { status: 200, schema: Margins },
    errors: [],
    emits: [],
  }),
  "finance.agentSpend": defineRoute({
    ...core,
    id: "finance.agentSpend",
    method: "get",
    path: `${base}/finance/agent-spend`,
    tag: "finance",
    summary:
      "Agent spend per DE/agent: today, the week, the month, averages, budget and pace (SPEND-KPIS)",
    description:
      "From the cost ledger's agent rows, summed per company-local day by the `spend.snapshot` " +
      "job. Managers and finance admins see every agent and the company totals; a seat holder " +
      "sees the agents on the seats under theirs, and totals over those.",
    access: "member",
    params: CompanyParams,
    query: AgentSpendQuery,
    response: { status: 200, schema: AgentSpend },
    errors: [],
    emits: [],
  }),
  "agent.spend": defineRoute({
    ...core,
    id: "agent.spend",
    method: "get",
    path: `${base}/agent/spend`,
    tag: "digital-employees",
    summary: "get_spend_stats: agent spend as the calling DE may see it (SPEND-KPIS)",
    description:
      "A digital employee's own run token or agent key. The company's Chief of Staff sees every " +
      "agent and the company totals; any other DE its own row, and totals over it.",
    access: "run_token",
    params: CompanyParams,
    query: AgentSpendQuery,
    response: { status: 200, schema: AgentSpend },
    errors: [],
    emits: [],
  }),
  "forecasts.list": defineRoute({
    ...core,
    id: "forecasts.list",
    method: "get",
    path: `${base}/finance/forecasts`,
    tag: "finance",
    summary: "The latest nightly forecast of a month (default: the current one)",
    access: "member",
    params: CompanyParams,
    query: ForecastQuery,
    response: { status: 200, schema: ForecastView },
    errors: [],
    emits: [],
  }),
  "scenarios.list": defineRoute({
    ...core,
    id: "scenarios.list",
    method: "get",
    path: `${base}/scenarios`,
    tag: "finance",
    summary: "Scenarios (restricted)",
    access: "member",
    params: CompanyParams,
    query: PageQuery,
    response: { status: 200, schema: page(Scenario) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "scenarios.create": defineRoute({
    ...core,
    id: "scenarios.create",
    method: "post",
    path: `${base}/scenarios`,
    tag: "finance",
    summary: "Create a scenario and evaluate it (finance_admin)",
    access: "member",
    params: CompanyParams,
    body: ScenarioInput,
    idempotency: "optional",
    response: { status: 201, schema: Scenario },
    errors: [],
    emits: ["scenario.created", "scenario.evaluated"],
  }),
  "scenarios.get": defineRoute({
    ...core,
    id: "scenarios.get",
    method: "get",
    path: `${base}/scenarios/{id}`,
    tag: "finance",
    summary: "A scenario with its last evaluation",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: Scenario },
    errors: [],
    emits: [],
  }),
  "scenarios.evaluate": defineRoute({
    ...core,
    id: "scenarios.evaluate",
    method: "post",
    path: `${base}/scenarios/{id}/evaluate`,
    tag: "finance",
    summary: "Re-evaluate a scenario against today's baseline (finance_admin)",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: Scenario },
    errors: [],
    emits: ["scenario.evaluated"],
  }),
  // ACTIONS: scenarios are what-ifs (nothing references one): edited in place, deleted outright.
  "scenarios.update": defineRoute({
    ...core,
    id: "scenarios.update",
    method: "patch",
    path: `${base}/scenarios/{id}`,
    tag: "finance",
    summary: "Edit a scenario and re-evaluate it (finance_admin)",
    access: "member",
    params: CompanyItemParams,
    body: ScenarioPatch,
    response: { status: 200, schema: Scenario },
    errors: [],
    emits: ["scenario.updated", "scenario.evaluated"],
  }),
  "scenarios.delete": defineRoute({
    ...core,
    id: "scenarios.delete",
    method: "delete",
    path: `${base}/scenarios/{id}`,
    tag: "finance",
    summary: "Delete a scenario (finance_admin)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 204 },
    errors: [],
    emits: ["scenario.deleted"],
  }),
  "headcountPlan.list": defineRoute({
    ...core,
    id: "headcountPlan.list",
    method: "get",
    path: `${base}/headcount-plan`,
    tag: "finance",
    summary: "Planned seats, human and digital (restricted)",
    access: "member",
    params: CompanyParams,
    query: HeadcountPlanQuery,
    response: { status: 200, schema: page(HeadcountPlan) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "headcountPlan.create": defineRoute({
    ...core,
    id: "headcountPlan.create",
    method: "post",
    path: `${base}/headcount-plan`,
    tag: "finance",
    summary: "Plan a seat (finance_admin)",
    access: "member",
    params: CompanyParams,
    body: HeadcountPlanInput,
    idempotency: "optional",
    response: { status: 201, schema: HeadcountPlan },
    errors: [],
    emits: ["headcount_plan.changed"],
  }),
  "headcountPlan.update": defineRoute({
    ...core,
    id: "headcountPlan.update",
    method: "patch",
    path: `${base}/headcount-plan/{id}`,
    tag: "finance",
    summary: "Change or archive a planned seat (finance_admin)",
    access: "member",
    params: CompanyItemParams,
    body: HeadcountPlanPatch,
    response: { status: 200, schema: HeadcountPlan },
    errors: ["conflict"],
    emits: ["headcount_plan.changed"],
  }),
  "headcountPlan.diff": defineRoute({
    ...core,
    id: "headcountPlan.diff",
    method: "get",
    path: `${base}/headcount-plan/diff`,
    tag: "finance",
    summary: "The plan for a month against the live org chart",
    access: "member",
    params: CompanyParams,
    query: HeadcountDiffQuery,
    response: { status: 200, schema: HeadcountDiff },
    errors: [],
    emits: [],
  }),
  "finance.export": defineRoute({
    ...core,
    id: "finance.export",
    method: "post",
    path: `${base}/finance/exports`,
    tag: "finance",
    summary: "Export ledger rows, revenue or the roll-up as CSV or XLSX with categories",
    access: "member",
    params: CompanyParams,
    body: FinanceExportInput,
    response: { status: 200, contentType: "application/octet-stream" },
    errors: [],
    emits: ["finance.exported"],
  }),
} as const;
